import React, { useEffect, useState } from 'react';
import { post, put } from "../../utils/fetchAPI";
import { toast } from "react-toastify";
import { FaTimes } from 'react-icons/fa';

// "ENT Specialist" -> "ent-specialist" (mirrors the API's toCode helper)
const toCode = (value) =>
    String(value)
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-|-$/g, "");

const SpecialityForm = ({ speciality, onSuccess, onClose }) => {
    const [specialityData, setSpecialityData] = useState({
        name: '',
        code: '',
        description: '',
        order: 1,
        active: true,
    });
    // Until the admin edits the code themselves it just tracks the name.
    const [codeTouched, setCodeTouched] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        if (speciality) {
            setSpecialityData({
                name: speciality.name || '',
                code: speciality.code || '',
                description: speciality.description || '',
                order: speciality.order ?? 1,
                active: speciality.active !== undefined ? speciality.active : true,
            });
            setCodeTouched(true);
        } else {
            setSpecialityData({
                name: '',
                code: '',
                description: '',
                order: 1,
                active: true,
            });
            setCodeTouched(false);
        }
    }, [speciality]);

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;

        if (name === 'name' && !codeTouched) {
            setSpecialityData((prev) => ({ ...prev, name: value, code: toCode(value) }));
            return;
        }

        if (name === 'code') {
            setCodeTouched(true);
        }

        setSpecialityData((prev) => ({
            ...prev,
            [name]: type === 'checkbox'
                ? checked
                : type === 'number'
                    ? (value === '' ? '' : Number(value))
                    : value,
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!specialityData.name.trim()) {
            toast.error('Speciality name is required!', {
                position: "top-center",
                theme: "light",
            });
            return;
        }

        const code = toCode(specialityData.code || specialityData.name);
        if (!code) {
            toast.error('Speciality code must contain at least one letter or number!', {
                position: "top-center",
                theme: "light",
            });
            return;
        }

        const dataToSend = {
            name: specialityData.name.trim(),
            code,
            description: specialityData.description.trim(),
            order: typeof specialityData.order === 'number' ? specialityData.order : Number(specialityData.order) || 1,
            active: specialityData.active,
        };

        setIsSaving(true);
        try {
            if (speciality) {
                await put(`/speciality/${speciality._id}`, dataToSend);
                toast.success('Speciality updated successfully!', {
                    position: "top-center",
                    theme: "light",
                    hideProgressBar: true,
                });
            } else {
                await post('/speciality/create', dataToSend);
                toast.success('Speciality created successfully!', {
                    position: "top-center",
                    theme: "light",
                    hideProgressBar: true,
                });
            }
            onSuccess();
            onClose();
        } catch (error) {
            console.error('Error saving speciality:', error);
            const errorMessage = error.response?.data?.errors?.[0]?.msg ||
                error.response?.data?.message ||
                'Failed to save speciality. Please try again.';
            toast.error(errorMessage, {
                position: "top-center",
                theme: "light",
                hideProgressBar: true,
            });
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className='py-6 min-[320px]:py-2 lg:px-5 min-[320px]:px-2'>
            <div className="flex justify-between items-center mb-4">
                <h2 className="text-blue-500 lg:text-3xl sm:text-lg font-bold">
                    {speciality ? "Edit Speciality" : "Create New Speciality"}
                </h2>
                <button
                    onClick={onClose}
                    className="text-gray-500 hover:text-gray-700"
                >
                    <FaTimes className="w-6 h-6" />
                </button>
            </div>

            <form className='text-black lg:text-sm min-[320px]:text-xs flex flex-col w-full' onSubmit={handleSubmit}>
                {/* Name */}
                <div className="mb-4">
                    <label className="block mb-2 font-medium">Name *</label>
                    <input
                        type="text"
                        name="name"
                        value={specialityData.name}
                        onChange={handleInputChange}
                        placeholder="e.g., Cardiologist, ENT Specialist"
                        className="w-full text-sm border p-2 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                        required
                    />
                </div>

                {/* Code */}
                <div className="mb-4">
                    <label className="block mb-2 font-medium">Code</label>
                    <input
                        type="text"
                        name="code"
                        value={specialityData.code}
                        onChange={handleInputChange}
                        placeholder="e.g., cardiologist"
                        className="w-full text-sm border p-2 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                        Generated from the name until you edit it. Changing the code on an existing
                        speciality will not update doctors already tagged with the old one.
                    </p>
                </div>

                {/* Description */}
                <div className="mb-4">
                    <label className="block mb-2 font-medium">Description</label>
                    <textarea
                        name="description"
                        value={specialityData.description}
                        onChange={handleInputChange}
                        rows={2}
                        placeholder="Optional note about this speciality"
                        className="w-full text-sm border p-2 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                </div>

                {/* Order */}
                <div className="mb-4">
                    <label className="block mb-2 font-medium">Order</label>
                    <input
                        type="number"
                        name="order"
                        value={specialityData.order}
                        onChange={handleInputChange}
                        placeholder="1"
                        min={1}
                        className="w-full text-sm border p-2 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                    <p className="text-xs text-gray-500 mt-1">Lower numbers appear first in the list</p>
                </div>

                {/* Active */}
                <div className="mb-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                        <input
                            type="checkbox"
                            name="active"
                            checked={specialityData.active}
                            onChange={handleInputChange}
                            className="w-4 h-4 text-primary focus:ring-primary rounded"
                        />
                        <span className="font-medium">Active</span>
                    </label>
                    <p className="text-xs text-gray-500 mt-1">Only active specialities are offered when tagging a doctor</p>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-4 mt-6 pt-4 border-t">
                    <button
                        type="submit"
                        disabled={isSaving}
                        className="text-sm px-6 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors disabled:opacity-60"
                    >
                        {isSaving ? 'Saving...' : speciality ? 'Update' : 'Create'}
                    </button>
                    <button
                        type="button"
                        onClick={onClose}
                        className="text-sm px-6 py-2 bg-gray-300 text-black rounded-md hover:bg-gray-400 transition-colors"
                    >
                        Cancel
                    </button>
                </div>
            </form>
        </div>
    );
};

export default SpecialityForm;
